import { useSearchParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/status";
import StatusBadge from "@/components/StatusBadge";
import { SearchForm } from "@/components/SearchForm";
import { ExternalLink, MapPin, Phone, Globe, Search as SearchIcon, Info } from "lucide-react";

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [sources, setSources] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const initial = Object.fromEntries([...params.entries()].filter(([, v]) => v && v !== "__any__"));
  const hasQuery = Object.keys(initial).length > 0;

  useEffect(() => {
    setLoading(true);
    setShowForm(false);
    const qp = new URLSearchParams(initial).toString();
    Promise.all([
      qp ? apiClient.get(`/prisoners/search?${qp}`).then(r => r.data.results || []).catch(() => []) : Promise.resolve([]),
      apiClient.get(`/state-sources`).then(r => r.data || []).catch(() => []),
    ]).then(([res, src]) => {
      setResults(res);
      setSources(src);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const criteria = Object.entries(initial).map(([k, v]) => `${k.replace("_", " ")}: ${v}`);
  const formOpen = showForm || !hasQuery;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="search-results-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Search official inmate locators</h1>
          <p className="mt-1 text-sm text-slate-600" data-testid="search-summary">
            Inmate records are held by government agencies. Use the official locators below to confirm custody and location.
          </p>
          {criteria.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="search-criteria">
              {criteria.map(c => <Badge key={c} variant="secondary" className="font-mono text-[10px] uppercase">{c}</Badge>)}
            </div>
          )}
        </div>
        {hasQuery && !showForm && (
          <button type="button" onClick={() => setShowForm(true)} className="text-sm font-medium text-blue-700 hover:underline" data-testid="back-to-search">
            ← New search
          </button>
        )}
      </div>

      {formOpen && (
        <div className="mt-6" data-testid="search-page-form">
          <SearchForm key={params.toString()} initial={initial} onSubmitted={() => setShowForm(false)} />
        </div>
      )}

      {hasQuery && (
        <div className="mt-6 space-y-4">
          {loading && <Skeleton className="h-56 w-full rounded-lg" />}
          {!loading && <LocatorCard params={params} sources={sources} onRefine={() => setShowForm(true)} />}
          {!loading && results.length > 0 && (
            <h2 className="pt-4 font-heading text-lg font-bold text-slate-900" data-testid="portal-records-heading">Records on file in this portal</h2>
          )}
          {!loading && results.map((p) => <ResultCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

function LocatorCard({ params, sources, onRefine }) {
  const first = params.get("first_name") || "";
  const last = params.get("last_name") || "";
  const mid = params.get("middle_name") || "";
  const stateParam = params.get("state") || "";
  const bopUrl = `https://www.bop.gov/inmateloc/?FirstName=${encodeURIComponent(first)}&LastName=${encodeURIComponent(last)}&Middle=${encodeURIComponent(mid)}&Race=U&Sex=U&Age=&x=0&y=0`;
  const stateCode = stateParam && stateParam.toLowerCase() !== "federal" ? stateParam.toUpperCase() : null;
  const stateSource = stateCode ? sources.find(s => s.code === stateCode) : null;

  return (
    <Card className="p-6 sm:p-8 border-2 border-blue-200 bg-blue-50" data-testid="locator-card">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-blue-600 text-white"><SearchIcon size={18} /></div>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-slate-900">Continue on the official locator</h2>
          <p className="mt-1 text-sm text-slate-700 leading-relaxed">
            Open the authoritative government locator to see current custody, register number, and assigned institution. Your name is pre-filled where the official site supports it. Then come back to check that facility's status and visiting rules.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        <a href={bopUrl} target="_blank" rel="noreferrer" data-testid="deeplink-bop">
          <Button className="w-full justify-start bg-slate-900 hover:bg-slate-800 text-white h-auto py-3">
            <ExternalLink size={14} className="mr-2 shrink-0" />
            <div className="text-left">
              <div className="text-sm font-semibold">Federal Bureau of Prisons</div>
              <div className="text-[11px] opacity-80 font-normal">bop.gov inmate locator</div>
            </div>
          </Button>
        </a>
        {stateSource && (
          <a href={stateSource.locator_url} target="_blank" rel="noreferrer" data-testid="deeplink-state">
            <Button variant="outline" className="w-full justify-start border-slate-900 text-slate-900 hover:bg-slate-100 h-auto py-3">
              <ExternalLink size={14} className="mr-2 shrink-0" />
              <div className="text-left">
                <div className="text-sm font-semibold">{stateSource.state} locator</div>
                <div className="text-[11px] opacity-80 font-normal">{stateSource.available ? "Official state locator" : "State DOC main site"}</div>
              </div>
            </Button>
          </a>
        )}
      </div>

      <div className="mt-5 border-t border-blue-200 pt-4">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-600 mb-2">
          <Info size={12} /> All state locators
        </div>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 max-h-64 overflow-y-auto">
          {sources.map(s => (
            <a key={s.code} href={s.locator_url} target="_blank" rel="noreferrer"
              className={`flex items-center justify-between rounded border bg-white p-2 text-xs hover:border-slate-400 ${!s.available ? "opacity-70" : ""}`}
              data-testid={`deeplink-source-${s.code}`}>
              <span className="font-medium text-slate-900 truncate">{s.state}</span>
              <ExternalLink size={10} className="text-blue-600 shrink-0" />
            </a>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onRefine} className="text-sm text-blue-700 hover:underline" data-testid="refine-search-link">← Refine search</button>
        <Link to="/facilities" className="text-sm text-blue-700 hover:underline" data-testid="browse-facilities-link">Know the facility? Check its status →</Link>
      </div>
    </Card>
  );
}

function ResultCard({ p }) {
  const f = p.facility;
  return (
    <Card className="p-5 sm:p-6 border-slate-200" data-testid={`result-card-${p.id}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-xl font-bold text-slate-900" data-testid="result-name">
              {p.first_name} {p.middle_name ? `${p.middle_name} ` : ""}{p.last_name}
            </h2>
          </div>
          <div className="mt-1 font-mono text-xs text-slate-600">
            <span className="uppercase tracking-widest">Inmate ID:</span> <span className="text-slate-900 font-semibold">{p.inmate_id}</span>
            {p.dob && <span className="ml-3"><span className="uppercase tracking-widest">DOB:</span> {p.dob}</span>}
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
            <div>
              <div className="text-xs uppercase font-semibold tracking-wide text-slate-500">Custody status</div>
              <div className="text-slate-900">{p.custody_status || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold tracking-wide text-slate-500">System</div>
              <div className="text-slate-900">{p.system}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold tracking-wide text-slate-500">Projected release</div>
              <div className="text-slate-900">{p.projected_release_date || "Not publicly available"}</div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold tracking-wide text-slate-500">Parole</div>
              <div className="text-slate-900">{p.parole_info || "Not publicly available"}</div>
            </div>
          </div>

          {f && (
            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="font-heading font-bold text-slate-900">{f.name}</div>
                <StatusBadge status={f.status} size="sm" />
              </div>
              <div className="mt-2 text-xs font-mono uppercase tracking-widest text-slate-500">Correctional Institution Address</div>
              <div className="text-sm text-slate-800">
                {[f.address, f.city, f.state && f.zip_code ? `${f.state} ${f.zip_code}` : f.state].filter(Boolean).join(", ")}
              </div>
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                {f.phone && <span className="inline-flex items-center gap-1"><Phone size={12} />{f.phone}</span>}
                {f.website && <a href={f.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Globe size={12} />Official website</a>}
                <span className="inline-flex items-center gap-1"><MapPin size={12} />{f.facility_type}</span>
              </div>
            </div>
          )}

          <div className="mt-3 text-xs text-slate-500 font-mono">
            Source: {p.source} · Last checked: {formatDate(p.last_checked_at)}
          </div>
        </div>

        <div className="flex flex-col gap-2 md:w-56">
          {f && (
            <Link to={`/facility/${f.id}`} data-testid={`view-facility-${p.id}`}>
              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">View facility &amp; visitation</Button>
            </Link>
          )}
          {p.official_record_url && (
            <a href={p.official_record_url} target="_blank" rel="noreferrer" data-testid={`verify-official-record-btn-${p.id}`}>
              <Button variant="outline" className="w-full border-blue-600 text-blue-700 hover:bg-blue-50">
                <ExternalLink size={14} className="mr-2" /> Verify official record
              </Button>
            </a>
          )}
          <Link to={`/facility/${f?.id}#tracker`} data-testid={`track-inmate-${p.id}`} className="w-full">
            <Button variant="outline" className="w-full border-amber-500 text-amber-700 hover:bg-amber-50">
              Track this inmate · $2/mo
            </Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}
